import { ButtonMenu, useModal } from "@homebound/beam";
import {
  createPlanPackageDetailsOverviewUrl,
  createPlanPackageEditUrl,
  createPlanPackageVersionHistoryUrl,
} from "src/RouteUrls";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { PlanPackageDetailsFragment } from "src/routes/libraries/plan-package/details/PlanPackagePage.generated";
import { ArchivePlanPackageModal } from "src/routes/libraries/plan-package/details/components/ArchivePlanPackageModal";
import { CopyPlanPackageModal } from "src/routes/libraries/plan-package/details/components/CopyPlanPackageModal";
import { useConfirmDiscardDraftVersionModalButtonMenuItem } from "./ConfirmDiscardDraftVersionModal";

type PlanPackageButtonMenuProps = {
  planPackage: Pick<
    PlanPackageDetailsFragment,
    | "id"
    | "aggregateLatest"
    | "status"
    | "name"
    | "type"
    | "displayCode"
    | "aggregateVersions"
    | "aggregateDraft"
    | "canEdit"
  >;
  // Renders trigger as vertical dots icon
  iconTrigger?: boolean;
  // If specified, redirects to the PP overview page of the latest version after discarding the draft
  redirectOnDiscardDraft?: true;
};

export function PlanPackageButtonMenu(props: PlanPackageButtonMenuProps) {
  const { iconTrigger, planPackage, redirectOnDiscardDraft } = props;
  const { openModal } = useModal();
  const discardDraftMenuItem = useConfirmDiscardDraftVersionModalButtonMenuItem(
    planPackage,
    redirectOnDiscardDraft ? createPlanPackageDetailsOverviewUrl : undefined,
  );
  return (
    <ButtonMenu
      trigger={iconTrigger ? { icon: "verticalDots" } : { label: "Actions", variant: "secondary", size: "md" }}
      // TODO: Work with Product & Design to determine if this set of actions is correct.
      // Thoughts: There is no way to move back to "Draft" if plan is in another status.
      // Does it even make sense to have separate actions for changing status? Design should really take another look.
      items={[
        // TODO: Should this be disabled for archived plans?
        { label: "Edit", onClick: createPlanPackageEditUrl(planPackage.id, planPackage.aggregateLatest?.id) },
        {
          label: "Version History",
          onClick: createPlanPackageVersionHistoryUrl(planPackage.id, planPackage.aggregateLatest?.id),
        },
        {
          label: "Archive",
          onClick: () => openModal({ content: <ArchivePlanPackageModal planPackageId={planPackage.id} /> }),
          disabled: planPackage.status.code === ReadyPlanStatus.Archived ? "Plan is already Archived" : false,
        },
        discardDraftMenuItem,
        {
          label: "Make a copy",
          onClick: () =>
            openModal({
              content: <CopyPlanPackageModal planPackage={planPackage} />,
              size: "lg",
            }),
        },
      ]}
    />
  );
}
